import React, { useState, useEffect } from 'react';
import { Table, message, Space, Button, DatePicker, Select, Row, Col, Tooltip } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { EyeOutlined, PrinterOutlined, PlusOutlined, EditOutlined, UnorderedListOutlined, SearchOutlined, ImportOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { useColumnManager } from '../../hooks';
import { PageTitle, StyledCard, ColumnManagerDropdown } from '../common';
import { theme } from '../../theme';

const { RangePicker } = DatePicker;

// Definir la interface para los datos
interface ClasificacionData {
  key: string;
  id: number;
  fecha: string;
  nro_planilla: number;
  origen: string;
  cliente: string;
  desde_fardo: number;
  hasta_fardo: number;
  nro_lote: number;
  lote_desde: number;
  lote_hasta: number;
  cantidad_fardos: number;
  calidad_desde: string;
  calidad_hasta: string;
  micronaire_desde: number;
  micronaire_hasta: number;
  longitud_desde: number;
  longitud_hasta: number;
  resistencia_desde: number;
  resistencia_hasta: number;
  calidad: string;
  detalles: {
    calidad: string;
  }[];
  alta: number;
}

interface ClasificacionDetalle {
  id: number;
  nro_lote: number;
  calidad: string;
  micronaire: number;
  longitud: number;
  resistencia: number;
  alta: number;
}

interface ClasificacionResponse {
  detalles: ClasificacionDetalle[];
}

interface ApiResponse {
  status: 'success' | 'error';
  message?: string;
}

interface Cliente {
  id: number;
  denominacion: string;
  nc: string;
}

const ListadoClasificacion: React.FC = () => {
  const [data, setData] = useState<ClasificacionData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  
  // Estados para filtros
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [fechaDesde, setFechaDesde] = useState<moment.Moment>(moment().subtract(30, 'days'));
  const [fechaHasta, setFechaHasta] = useState<moment.Moment>(moment());
  const [clienteSeleccionado, setClienteSeleccionado] = useState<number | null>(null);
  const [estadoSeleccionado, setEstadoSeleccionado] = useState<number | null>(null);

  // Lista de todas las columnas disponibles (keys)
  const allColumnKeys = [
    'id',
    'fecha',
    'nro_planilla',
    'origen',
    'cliente',
    'fardos',
    'lotes',
    'cantidad_fardos',
    'calidad',
    'micronaire',
    'longitud',
    'resistencia',
    'estado',
    'acciones'
  ];

  // Nombres amigables para las columnas
  const columnNames: Record<string, string> = {
    'id': 'ID',
    'fecha': 'Fecha',
    'nro_planilla': 'N° Planilla',
    'origen': 'Origen',
    'cliente': 'Cliente',
    'fardos': 'Fardos',
    'lotes': 'Lotes',
    'cantidad_fardos': 'Cantidad',
    'calidad': 'Calidad',
    'micronaire': 'Micronaire',
    'longitud': 'Longitud',
    'resistencia': 'Resistencia',
    'estado': 'Estado',
    'acciones': 'Acciones'
  };

  // Visibilidad + orden de columnas (persistido) — reemplaza el manejo manual de antes.
  const columnManager = useColumnManager({
    storageKey: 'clasificacion',
    allKeys: allColumnKeys,
    requiredKeys: ['id', 'acciones'],
  });

  // Función para filtrar columnas basándose en la visibilidad
  const isColumnVisible = (key: string): boolean => {
    return columnManager.orderedVisibleKeys.includes(key);
  };

  // Definir las columnas de la tabla
  const allColumns: ColumnsType<ClasificacionData> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      fixed: 'left',
      align: 'right',
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      render: (fecha: string) => moment(fecha).format('DD/MM/YYYY'),
      width: 100,
      fixed: 'left',
    },
    {
      title: 'N° Planilla',
      dataIndex: 'nro_planilla',
      key: 'nro_planilla',
      width: 90,
      fixed: 'left',
    },
    {
      title: 'Origen',
      dataIndex: 'origen',
      key: 'origen',
      width: 150,
    },
    {
      title: 'Cliente',
      dataIndex: 'cliente',
      key: 'cliente',
      width: 150,
    },
    {
      title: 'Fardos',
      children: [
        {
          title: 'Desde',
          dataIndex: 'desde_fardo',
          key: 'desde_fardo',
          width: 80,
          align: 'right',
        },
        {
          title: 'Hasta',
          dataIndex: 'hasta_fardo',
          key: 'hasta_fardo',
          width: 80,
          align: 'right',
        },
      ],
    },
    {
      title: 'Lotes',
      children: [
        {
          title: 'Desde',
          dataIndex: 'lote_desde',
          key: 'lote_desde',
          width: 80,
          align: 'right',
        },
        {
          title: 'Hasta',
          dataIndex: 'lote_hasta',
          key: 'lote_hasta',
          width: 80,
          align: 'right',
        },
      ],
    },
    {
      title: 'Cantidad',
      dataIndex: 'cantidad_fardos',
      key: 'cantidad_fardos',
      width: 90,
      align: 'right',
    },
    {
      title: 'Calidad',
      children: [
        {
          title: 'Desde',
          dataIndex: 'calidad_desde',
          key: 'calidad_desde',
          width: 80,
          align: 'center',
        },
        {
          title: 'Hasta',
          dataIndex: 'calidad_hasta',
          key: 'calidad_hasta',
          width: 80,
          align: 'center',
        },
      ],
    },
    {
      title: 'Micronaire',
      children: [
        {
          title: 'Desde',
          dataIndex: 'micronaire_desde',
          key: 'micronaire_desde',
          width: 90,
          align: 'right',
          render: (value: number) => value.toFixed(2),
        },
        {
          title: 'Hasta',
          dataIndex: 'micronaire_hasta',
          key: 'micronaire_hasta',
          width: 90,
          align: 'right',
          render: (value: number) => value.toFixed(2),
        },
      ],
    },
    {
      title: 'Longitud',
      children: [
        {
          title: 'Desde',
          dataIndex: 'longitud_desde',
          key: 'longitud_desde',
          width: 90,
          align: 'right',
          render: (value: number) => value.toFixed(2),
        },
        {
          title: 'Hasta',
          dataIndex: 'longitud_hasta',
          key: 'longitud_hasta',
          width: 90,
          align: 'right',
          render: (value: number) => value.toFixed(2),
        },
      ],
    },
    {
      title: 'Resistencia',
      children: [
        {
          title: 'Desde',
          dataIndex: 'resistencia_desde',
          key: 'resistencia_desde',
          width: 90,
          align: 'right',
          render: (value: number) => value.toFixed(2),
        },
        {
          title: 'Hasta',
          dataIndex: 'resistencia_hasta',
          key: 'resistencia_hasta',
          width: 90,
          align: 'right',
          render: (value: number) => value.toFixed(2),
        },
      ],
    },
    {
      title: 'Estado',
      dataIndex: 'alta',
      key: 'estado',
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (alta: number | null | undefined) => {
        // Si alta es null o undefined, consideramos que está activo (1)
        const estado = alta === 0 ? 0 : 1;
        return (
          <span style={{
            color: estado === 1 ? theme.colors.success : theme.colors.error,
            fontWeight: 'bold'
          }}>
            {estado === 1 ? 'Activo' : 'Inactivo'}
          </span>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 140,
      fixed: 'right' as const,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => navigate(`/TrabajoClasificacion/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Emitir">
            <Button
              icon={<PrinterOutlined />}
              size="small"
              onClick={() => navigate(`/EmisionClasificacion/${record.id}`)}
              style={{
                backgroundColor: theme.colors.secondary,
                borderColor: theme.colors.secondary,
                color: 'white'
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Título de columnas agrupadas -> clave del selector de columnas
  const groupTitleToKey: Record<string, string> = {
    'Fardos': 'fardos',
    'Lotes': 'lotes',
    'Calidad': 'calidad',
    'Micronaire': 'micronaire',
    'Longitud': 'longitud',
    'Resistencia': 'resistencia',
  };

  // Columnas visibles, en el orden elegido por el usuario en el selector de columnas.
  // Las columnas agrupadas (Fardos/Lotes/Calidad/Micronaire/Longitud/Resistencia) se mueven como un solo bloque.
  const columnsByKey = new Map<string, (typeof allColumns)[number]>();
  allColumns.forEach(col => {
    if ('children' in col && col.children) {
      const title = typeof col.title === 'string' ? col.title : '';
      const mappedKey = groupTitleToKey[title];
      if (mappedKey) columnsByKey.set(mappedKey, col);
    } else if (col.key) {
      columnsByKey.set(col.key as string, col);
    }
  });
  const columns = columnManager.orderedVisibleKeys
    .map(key => columnsByKey.get(key))
    .filter((col): col is (typeof allColumns)[number] => Boolean(col));


  // Cargar clientes al montar el componente
  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get(
          `${process.env.REACT_APP_PADRON_URL}/leer_padron/${user?.id_empresa}/`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setClientes(response.data || []);
      } catch (error) {
        console.error('Error al cargar los clientes:', error);
        message.error('Error al cargar la lista de clientes');
      }
    };

    if (user?.id_empresa) {
      fetchClientes();
    }
  }, [user, getAccessTokenSilently]);

  // Función para buscar clasificaciones con filtros
  const handleBuscar = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      // Construir parámetros de búsqueda
      const params = new URLSearchParams();
      params.append('fecha_desde', fechaDesde.format('YYYY-MM-DD'));
      params.append('fecha_hasta', fechaHasta.format('YYYY-MM-DD'));
      if (clienteSeleccionado) {
        params.append('cliente_id', clienteSeleccionado.toString());
      }
      if (estadoSeleccionado) {
        params.append('estado', estadoSeleccionado.toString());
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_CLASIFICACION_URL}/listar_clasificaciones/${user?.id_empresa}?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (Array.isArray(response.data)) {
        const dataConKey = response.data.map((item: any) => ({
          ...item,
          key: `${item.fecha}-${item.nro_planilla}`,
        }));
        setData(dataConKey);
      } else {
        throw new Error('La respuesta no es un array');
      }
    } catch (error) {
      console.error('Error al cargar las clasificaciones:', error);
      message.error('Error al cargar el listado de clasificaciones');
    } finally {
      setLoading(false);
    }
  };

  // Manejadores para los botones
  const handleNuevaClasificacion = () => {
    navigate('/FormularioClasificacion');
  };

  const handleLotes = () => {
    navigate('/emision_lotes');
  };

  const handleImportar = () => {
    navigate('/ImportarClasificacion');
  };

  return (
    <div style={{ padding: '0' }}>
      <PageTitle>Listado de Clasificaciones</PageTitle>

      {/* Botones de acciones generales */}
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleNuevaClasificacion}
          size="small"
          style={{ backgroundColor: theme.colors.primary, borderColor: theme.colors.primary }}
        >
          Nuevo
        </Button>
        <Button
          icon={<UnorderedListOutlined />}
          onClick={handleLotes}
          size="small"
        >
          Lotes
        </Button>
        <Button
          icon={<ImportOutlined />}
          onClick={handleImportar}
          size="small"
        >
          Importar
        </Button>
      </div>

      {/* Sección de Filtros */}
      <StyledCard title="Filtros de Búsqueda">
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col xs={24} sm={12} md={6} lg={4}>
          <div style={{ marginBottom: '4px', fontWeight: 500 }}>Desde Fecha</div>
          <DatePicker
            value={fechaDesde}
            onChange={(date) => date && setFechaDesde(date)}
            format="DD/MM/YYYY"
            style={{ width: '100%' }}
            placeholder="Fecha Desde"
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <div style={{ marginBottom: '4px', fontWeight: 500 }}>Hasta Fecha</div>
          <DatePicker
            value={fechaHasta}
            onChange={(date) => date && setFechaHasta(date)}
            format="DD/MM/YYYY"
            style={{ width: '100%' }}
            placeholder="Fecha Hasta"
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <div style={{ marginBottom: '4px', fontWeight: 500 }}>Cliente</div>
          <Select
            showSearch
            allowClear
            placeholder="Seleccione un cliente"
            style={{ width: '100%' }}
            value={clienteSeleccionado}
            onChange={(value) => setClienteSeleccionado(value)}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={clientes.map(cliente => ({
              value: cliente.id,
              label: `${cliente.denominacion} - ${cliente.nc}`
            }))}
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <div style={{ marginBottom: '4px', fontWeight: 500 }}>Estado</div>
          <Select
            allowClear
            placeholder="Seleccione un estado"
            style={{ width: '100%' }}
            value={estadoSeleccionado}
            onChange={(value) => setEstadoSeleccionado(value)}
            options={[
              { value: 1, label: 'Faltantes de Grado' },
              { value: 2, label: 'Faltante Loteo' },
              { value: 3, label: 'Faltante Grado y Loteo' },
              { value: 4, label: 'Faltante de Origen y Cliente' }
            ]}
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <div style={{ marginBottom: '4px', fontWeight: 500 }}>&nbsp;</div>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleBuscar}
            size="small"
            style={{ width: '100%' }}
          >
            Buscar
          </Button>
        </Col>
      </Row>
      </StyledCard>

      <StyledCard 
        title="Clasificaciones"
        bodyStyle={{ padding: 0 }}
        extra={
          <ColumnManagerDropdown
            manager={columnManager}
            labels={columnNames}
            requiredKeys={['id', 'acciones']}
            buttonSize="small"
          />
        }
      >
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        scroll={{ x: 'max-content', y: 'max(150px, calc(100vh - 450px))' }}
        pagination={{
          position: ['bottomRight'],
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`,
          defaultPageSize: 50,
        } as TablePaginationConfig}
        size="middle"
      />
      </StyledCard>
    </div>
  );
};

export default ListadoClasificacion;
